import React, { FunctionComponent, useContext, useEffect } from 'react'
import { FormControl, IconButton, Menu, MenuItem, Select, SelectChangeEvent, Stack, Typography } from '@mui/material'
import { IReportAction } from '../../../../../shared/services/report-action/report-action.model'
import { IUserDetails } from '../../../../../shared/services/user-details/user-details.model'
import { UserNotificationsContext } from '../../../../../context/user-notifications/user-notifications'
import ReportActionService from '../../../../../shared/services/report-action/report-action.service'
import { AvatarDisplay } from '../../../display-elements-components/avatar-display'

// The action owner control: avatar plus name, opening the assignee dropdown. Extracted from
// the action card heading so the readiness Tasks tab shares the exact same behaviour,
// including the bulk "assign this user to all outstanding and upcoming actions of this type"
// prompt for monthly recurring actions.
export const AssignActionOwner: FunctionComponent<{
  reportAction: IReportAction,
  users: IUserDetails[],
  disabled?: boolean,
  onAssigned?: () => void,
}> = ({
  reportAction,
  users,
  disabled = false,
  onAssigned,
}) => {

    const userNotificationsContext = useContext(UserNotificationsContext)
    const reportActionService = new ReportActionService();
    const [userId, setUserId] = React.useState<string>()
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const openDropDown = Boolean(anchorEl);

    useEffect(() => {
      setUserId(reportAction.user ? reportAction.user.id : 'unassigned')
    }, [reportAction])

    const menuCloseHandler = () => {
      setAnchorEl(null)
    }

    const assign = (userIdToAssign: string, assignToAllActions: boolean) => {
      reportActionService.assignUserToActions(reportAction.id, userIdToAssign, assignToAllActions)
        .then(() => {
          if (onAssigned) {
            onAssigned()
          }
        })
        .catch(err => {
          userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
          console.log(err)
        })
    }

    const selectHandler = (e: SelectChangeEvent<string>) => {
      if (reportAction.actionBillingPeriod === 'monthly') {
        // Monthly actions repeat, so offer to hand the whole run to the same owner. The prompt
        // states the exact number it would cover; when this is the only incomplete one left
        // there is nothing to expand, so no prompt.
        reportActionService.getBulkAssignScope(reportAction.id)
          .then(({ count }) => {
            if (count <= 1) {
              setUserId(e.target.value)
              assign(e.target.value, false)
              return
            }
            userNotificationsContext.openDialog({
              active: true,
              title: 'This is a monthly task',
              message: `There are ${count} outstanding and upcoming months of this task. Do you want to assign this person to all of them, or just this one?`,
              buttonCopy: `Assign all ${count}`,
              cancelButtonCopy: 'Only this one',
            }).then(response => {
              if (!response) {
                setUserId(e.target.value)
              }
              assign(e.target.value, !!response)
            })
          })
          .catch(() => {
            // If the count lookup fails, fall back to assigning just the clicked action rather
            // than blocking the assignment or guessing at a bulk update.
            setUserId(e.target.value)
            assign(e.target.value, false)
          })
      } else {
        setUserId(e.target.value)
        assign(e.target.value, false)
      }
    }

    return (
      <>
        {/* The whole avatar-plus-name row opens the picker (a bare 25px avatar is a small
            target, and "Unassigned" reads as the thing to click). Disabled MUI buttons have
            pointer-events none, so the guard below keeps a disabled control inert even though
            the click then lands on the Stack. */}
        <Stack
          display='flex'
          flexDirection='row'
          gap={1}
          alignItems='center'
          onClick={(e) => {
            e.stopPropagation();
            if (!disabled) {
              setAnchorEl(e.currentTarget);
            }
          }}
          sx={{ cursor: disabled ? 'default' : 'pointer', width: 'fit-content' }}
        >
          <IconButton sx={{ p: 0 }} disabled={disabled}>
            {reportAction.user
              ? <AvatarDisplay color={reportAction.user.avatarColor} width={'25px'} height={'25px'} padding={0} userDetails={reportAction.user} fontSize={'0.75rem'} hover={!disabled} />
              : <AvatarDisplay color={'#e7e7e7'} width={'25px'} height={'25px'} padding={0} fontSize={'0.75rem'} hover={!disabled} />}
          </IconButton>
          <Typography variant='body2' sx={{ color: 'text.secondary', '&:hover': disabled ? undefined : { textDecoration: 'underline' } }}>
            {reportAction.user ? `${reportAction.user.firstName} ${reportAction.user.surname}` : 'Unassigned'}
          </Typography>
        </Stack>
        {/* The Menu portals to the body, but React still bubbles its synthetic events through
            the REACT tree, i.e. through this component's parent. Inside a clickable table row
            that meant choosing an assignee (or closing the menu on its backdrop) also selected
            the row and navigated away, so every click inside the menu is stopped here. */}
        <Menu anchorEl={anchorEl} open={openDropDown} onClose={menuCloseHandler} elevation={3} onClick={(e) => e.stopPropagation()}>
          <MenuItem onClick={menuCloseHandler}>
            <FormControl fullWidth>
              <Select value={userId || ''} size='small' onChange={selectHandler}>
                {userId === 'unassigned' && <MenuItem sx={{ color: 'text.primary' }} disabled value='unassigned'>
                  <Stack display='flex' direction='row' spacing={2}>
                    <AvatarDisplay color={'#e7e7e7'} width={'25px'} height={'25px'} padding={0} fontSize={'0.75rem'} />
                    <Typography>Unassigned</Typography>
                  </Stack>
                </MenuItem>}
                {users.map(user => {
                  return (
                    <MenuItem key={user.id} value={user.id} sx={{ color: 'text.primary' }}>
                      <Stack display='flex' direction='row' spacing={2}>
                        <AvatarDisplay color={user.avatarColor} width={'25px'} height={'25px'} padding={0} userDetails={user} fontSize={'0.75rem'} />
                        <Typography>{user.firstName} {user.surname}</Typography>
                      </Stack>
                    </MenuItem>)
                })}
              </Select>
            </FormControl >
          </MenuItem>
        </Menu>
      </>
    )
  }
